import * as z from "zod/v4/mini";
import { envConfigSchema, TypeEnvConfig } from "./env.types";
import { getRevision } from "./revision.utils";

export * from "./env.types";

const ENV_CONFIG_PATH = "env/env.json";
const ENV_DEFAULT_CONFIG_PATH = "env/env.default.json";
const ENV_FIELD_NAME = "env";
const FETCH_TIMEOUT_MS = 1000;

const fetchJsonConfig = async (path: string): Promise<unknown | null> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
        const response = await fetch(`${path}?v=${await getRevision()}`, { cache: "no-store", signal: controller.signal });

        if (!response.ok) {
            throw new Error(`Unexpected status ${response.status}`);
        }

        return await response.json();
    } catch {
        console.warn(`[Bash] Unable to load "${path}".`);
        return null;
    } finally {
        clearTimeout(timeoutId);
    }
};

const fetchRawEnvConfig = async (): Promise<Record<string, unknown>> => {
    const envConfig = await fetchJsonConfig(ENV_CONFIG_PATH);
    const rawConfig = envConfig ?? await fetchJsonConfig(ENV_DEFAULT_CONFIG_PATH) ?? {};

    return typeof rawConfig === "object" && rawConfig !== null ? rawConfig as Record<string, unknown> : {};
};

const parseSection = (schema: z.core.$ZodType, fieldName: string, value: unknown): unknown => {
    if (value === undefined) {
        return undefined;
    }

    const result = z.safeParse(schema, value);

    if (!result.success) {
        console.warn(`[Bash] "${fieldName}" in env config failed schema validation, ignoring it.`, z.flattenError(result.error));
        return undefined;
    }

    return result.data;
};

const validateEnvConfig = (rawConfig: Record<string, unknown>): TypeEnvConfig => {
    const env = typeof rawConfig[ENV_FIELD_NAME] === "string" ? rawConfig[ENV_FIELD_NAME] as string : "undefined";
    const config: Record<string, unknown> = { [ENV_FIELD_NAME]: env };

    for (const [fieldName, schema] of Object.entries(envConfigSchema.shape)) {
        if (fieldName === ENV_FIELD_NAME) {
            continue;
        }

        config[fieldName] = parseSection(schema, fieldName, rawConfig[fieldName]);
    }

    return config as TypeEnvConfig;
};

export const getEnvConfig = async (): Promise<TypeEnvConfig> => {
    const rawConfig = await fetchRawEnvConfig();
    const config = validateEnvConfig(rawConfig);

    console.warn(`[Bash] env: "${config.env}"`);

    return config;
};
